import { createRoute, Link } from "@tanstack/solid-router";
import { createSignal, Show } from "solid-js";
import { authRoute } from "./route";
import { auth } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@fleet/ui";
import { msg } from "@lingui/core/macro";
import { AnimatedSection } from "@/components/ui/animated-section";
import { AuthScene } from "@/components/auth/auth-scene";

function ForgotPasswordPage() {
  const { t } = useI18n();
  const [email, setEmail] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  const [loading, setLoading] = createSignal(false);
  const [sent, setSent] = createSignal(false);

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await auth.forgotPassword({
        email: email(),
      });
      setSent(true);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t(msg`Unexpected error. Please try again.`));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthScene
      eyebrow={t(msg`Account recovery`)}
      headline={t(msg`Don't lose your momentum.`)}
      tagline={t(msg`Locked out? We'll send you a link so you can get back to your streak.`)}
      footer={
        <p class="text-xs text-muted-foreground/60">
          <Link to="/" class="transition-colors hover:text-primary">
            {t(msg`← Back to home`)}
          </Link>
        </p>
      }
    >
      <Show
        when={!sent()}
        fallback={
          <div class="space-y-4">
            <div class="space-y-1 rounded-lg border border-border bg-muted/40 p-4 text-center text-sm">
              <p class="font-medium">{t(msg`Email sent`)}</p>
              <p class="text-muted-foreground">
                {t(msg`Check your inbox at`)} <span class="font-medium">{email()}</span>{" "}
                {t(msg`and follow the link to reset your password.`)}
              </p>
            </div>
            <Link to="/login" class="block w-full">
              <Button variant="outline" class="w-full">
                {t(msg`Back to sign in`)}
              </Button>
            </Link>
          </div>
        }
      >
        <form onSubmit={(e) => void handleSubmit(e)} class="space-y-5">
          <div class="space-y-4">
            <div class="space-y-2">
              <Label for="email">{t(msg`Email`)}</Label>
              <TextField
                id="email"
                type="email"
                placeholder={t(msg`your@email.com`)}
                autocomplete="email"
                required
                disabled={loading()}
                value={email()}
                onInput={(e) => setEmail(e.currentTarget.value)}
              />
            </div>
            <AnimatedSection show={Boolean(error())}>
              <p class="text-sm text-destructive">{error()}</p>
            </AnimatedSection>
          </div>

          <div class="flex flex-col gap-3">
            <Button type="submit" class="w-full" loading={loading()}>
              {loading() ? t(msg`Sending…`) : t(msg`Send link`)}
            </Button>
            <p class="text-center text-sm text-muted-foreground">
              <Link to="/login" class="font-medium text-primary underline-offset-4 hover:underline">
                {t(msg`Back to sign in`)}
              </Link>
            </p>
          </div>
        </form>
      </Show>
    </AuthScene>
  );
}

export const forgotPasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});
